// MapPane.jsx
import React, { useEffect, useMemo } from "react";
import L from "leaflet";
import { MapContainer, TileLayer, GeoJSON, useMap } from "react-leaflet";

function InvalidateOnOpen({ isOpen }) {
  const map = useMap();
  useEffect(() => {
    if (!isOpen) return;
    requestAnimationFrame(() => requestAnimationFrame(() => map.invalidateSize(true)));
    
    // Additional fix for iOS Safari address bar changes
    const handleResize = () => {
      requestAnimationFrame(() => map.invalidateSize(true));
    };

    window.addEventListener('resize', handleResize);
    // Also listen for orientation changes on mobile
    window.addEventListener('orientationchange', handleResize);
    // iOS visual viewport resize fires when the address bar collapses/expands
    window.visualViewport?.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      window.visualViewport?.removeEventListener('resize', handleResize);
    };
  }, [isOpen, map]);
  return null;
}

function FitToGeoJson({ geojson, isOpen }) {
  const map = useMap();

  useEffect(() => {
    if (!isOpen) return;
    if (!geojson?.features?.length) return;

    // Ensure Leaflet has correct container size (especially in overlays / iPhone)
    requestAnimationFrame(() => requestAnimationFrame(() => map.invalidateSize(true)));

    const layer = L.geoJSON(geojson);
    const bounds = layer.getBounds();
    if (bounds && bounds.isValid()) {
      map.fitBounds(bounds.pad(0.06), { animate: false });
    }

  }, [geojson, isOpen, map]);

  return null;
}

export default function MapPane({
  isOpen,
  lotsGeoJson,
  selectedLotId,
  hoverLotId,
  onSelectLot,
  onHoverLot,
  onLotInfo, // <-- NEW
}) {
  const styleFor = useMemo(() => {
    return (feature) => {
      const lotId = feature.properties?.lotId ?? feature.id;
      const isSelected = parseInt(lotId, 10) === parseInt(selectedLotId, 10);
      const isHover = parseInt(lotId, 10) === parseInt(hoverLotId, 10);
      return {
        weight: isSelected ? 3 : 2,
        color: isSelected ? "#ffbb00ff" : "#3388ff",
        opacity: 1,
        fillOpacity: isSelected ? 0.35 : isHover ? 0.25 : 0.18,
      };
    };
  }, [selectedLotId, hoverLotId]);

  const onEachFeature = (feature, layer) => {
    const lotId = feature.properties?.lotId ?? feature.id;
    const lotLabel = feature.properties?.lotLabel ?? `Lot ${lotId}`;
    const ownersDisplay = feature.properties?.ownersDisplay ?? "";

    // More robust event handling for mobile
    const handleLotClick = (e) => {
      // Prevent event from bubbling
      if (e.originalEvent) {
        e.originalEvent.preventDefault();
        e.originalEvent.stopPropagation();
      }
      
      onSelectLot?.(lotId);
      onLotInfo?.({
        lotId,
        lotLabel,
        ownersDisplay,
        latlng: e?.latlng ?? null,
      });
    };

    layer.on({
      click: handleLotClick,
      tap: handleLotClick,  // Explicit tap handler for mobile

      // Desktop hover (won't fire on iPhone, which is fine)
      mouseover: () => onHoverLot?.(lotId),
      mouseout: () => onHoverLot?.(null),
    });

    // Optional: keep hover tooltip for desktop only
    // (It won’t hurt mobile; it just won’t show.)
    layer.bindTooltip(
      `<div style="font-weight:600">${lotLabel}</div>${ownersDisplay ? `<div>${ownersDisplay}</div>` : ""}`,
      { sticky: true }
    );
  };

  return (
    <MapContainer
      style={{ height: "100%", width: "100%" }}
      center={[30.5432, -98.3661]}
      zoom={16}
      scrollWheelZoom={true}
      tap={true}
      tapTolerance={15}
      preferCanvas={false}
      dragging={true}
      touchZoom={true}
      doubleClickZoom={false}
      zoomControl={true}
    >
      <InvalidateOnOpen isOpen={isOpen} />

      <FitToGeoJson geojson={lotsGeoJson} isOpen={isOpen} />

      <TileLayer
        attribution="&copy; OpenStreetMap contributors"
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />

      {lotsGeoJson ? (
        <GeoJSON 
          key={`geojson-${lotsGeoJson.features?.length || 0}`}
          data={lotsGeoJson} 
          style={styleFor} 
          onEachFeature={onEachFeature} 
        />
      ) : null}
    </MapContainer>
  );
}
